
import type { SvgIconComponent } from "@mui/icons-material";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import BabyChangingStationIcon from "@mui/icons-material/BabyChangingStation";
import CakeIcon from "@mui/icons-material/Cake";
import CelebrationIcon from "@mui/icons-material/Celebration";
import ChildCareIcon from "@mui/icons-material/ChildCare";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import DescriptionIcon from "@mui/icons-material/Description";
import DirectionsIcon from "@mui/icons-material/Directions";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import ExploreIcon from "@mui/icons-material/Explore";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import FavoriteIcon from "@mui/icons-material/Favorite";
import HelpOutlineIcon from "@mui/icons-material/HelpOutlined";
import HistoryIcon from "@mui/icons-material/History";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import HomeIcon from "@mui/icons-material/Home";
import LocalFloristIcon from "@mui/icons-material/LocalFlorist";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import MoodIcon from "@mui/icons-material/Mood";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import NatureIcon from "@mui/icons-material/Nature";
import PaletteIcon from "@mui/icons-material/Palette";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import PetsIcon from "@mui/icons-material/Pets";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import SchoolIcon from "@mui/icons-material/School";
import SentimentNeutralIcon from "@mui/icons-material/SentimentNeutral";
import SentimentVerySatisfiedIcon from "@mui/icons-material/SentimentVerySatisfied";
import SpaIcon from "@mui/icons-material/Spa";
import SportsIcon from "@mui/icons-material/Sports";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import TagFacesIcon from "@mui/icons-material/TagFaces";
import TerrainIcon from "@mui/icons-material/Terrain";
import TheaterComedyIcon from "@mui/icons-material/TheaterComedy";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import WomanIcon from "@mui/icons-material/Woman";
import WorkIcon from "@mui/icons-material/Work";

import type { LyricsChipTagType } from "~/types";

export type TagPalette = {
  icon: SvgIconComponent | null;
  color?: string; // e.g. 'primary.main' (bg for filled chips)
  contrastText?: string; // e.g. 'primary.contrastText'
  hoverColor?: string; // e.g. 'primary.dark'
};

const getMoodIcon = (mood: string) => {
  const normalize = (s: string) =>
    s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const m = normalize(mood || "");

  switch (m) {
    case "vesel":
    case "veselia":
      return SentimentVerySatisfiedIcon;

    case "linistitor":
    case "linistit":
      return SpaIcon;

    case "educativ":
    case "educational":
      return SchoolIcon;

    case "jucaus":
      return SportsIcon;

    case "energic":
      return WhatshotIcon;

    case "optimist":
      return WbSunnyIcon;

    case "nostalgic":
    case "nostalgie":
      return HistoryIcon;

    case "afectuos":
    case "tandru":
    case "duios":
    case "afectiune":
    case "iubire":
    case "recunostinta":
      return FavoriteIcon;

    case "interactiv":
      return TouchAppIcon;

    case "narativ":
    case "povestitor":
    case "povestire":
      return MenuBookIcon;

    case "ritmic":
      return MusicNoteIcon;

    case "umoristic":
    case "umor":
      return EmojiEmotionsIcon;

    case "ironic":
    case "ironia":
      return SentimentNeutralIcon;

    case "traditional":
    case "tradicional":
      return HistoryEduIcon;

    case "natura":
      return NatureIcon;

    case "misterios":
    case "misterios":
      return HelpOutlineIcon;

    case "obraznic":
    case "nazdravan":
      return TagFacesIcon;

    case "absurd":
      return TheaterComedyIcon;

    case "de iarna":
    case "iarna":
      return AcUnitIcon;

    case "festiv":
      return CelebrationIcon;

    case "sarbatoare":
    case "sarbatori":
      return CakeIcon;

    case "dansant":
      return MusicNoteIcon;

    case "incurajator":
    case "incurajare":
      return ThumbUpIcon;

    case "aventuros":
    case "aventura":
      return ExploreIcon;

    case "emotionant":
    case "cald":
      return FavoriteIcon;

    case "descriptiv":
      return DescriptionIcon;

    default:
      return MoodIcon;
  }
};

const getKeywordIcon = (keyword: string) => {
  const normalize = (s: string) =>
    s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const key = normalize(keyword);

  switch (key) {
    // Categories (from frontend/data/lyrics/_categories.json)
    case "animale":
      return PetsIcon;
    case "educational":
      return SchoolIcon;
    case "cantece de leagan":
      return ChildCareIcon;
    case "joc si dans":
      return MusicNoteIcon;
    case "sarbatori":
      return CelebrationIcon;
    case "natura":
      return NatureIcon;
    case "familie":
      return FamilyRestroomIcon;
    case "vehicule":
      return DirectionsCarIcon;
    case "povesti":
      return MenuBookIcon;
    case "meserii":
      return WorkIcon;
    case "corpul uman":
      return PersonIcon;
    case "prietenie":
      return PeopleIcon;
    case "igiena":
      return CleaningServicesIcon;

    // Other keywords / synonyms
    case "caini":
    case "pisica":
    case "soricel":
      return PetsIcon;
    case "culori":
      return PaletteIcon;
    case "copii":
      return ChildCareIcon;
    case "job":
    case "meserie":
      return WorkIcon;
    case "acasa":
      return HomeIcon;
    case "bucurie":
      return SentimentVerySatisfiedIcon;
    case "dans":
    case "joc":
    case "joaca":
      return SportsIcon;
    case "flori":
      return LocalFloristIcon;
    case "soare":
      return WbSunnyIcon;
    case "ursulet":
      return NatureIcon;
    case "mama":
      return WomanIcon;
    case "iarna":
    case "fulgi de nea":
      return AcUnitIcon;
    case "craciun":
    case "distractie":
    case "distractie":
      return CelebrationIcon;
    case "mancare":
      return RestaurantIcon;
    case "gradinita":
      return SchoolIcon;
    case "amici":
      return PeopleIcon;
    case "munte":
      return TerrainIcon;
    case "calatorie":
      return DirectionsIcon;
    default:
      return null;
  }
};

const getTargetAgeIcon = (age: string): SvgIconComponent => {
  const normalize = (s: string) =>
    s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const a = normalize(age);
  if (a.startsWith("0-2")) return BabyChangingStationIcon;
  if (a.startsWith("3-5")) return ChildCareIcon;
  if (a.startsWith("6-9")) return SchoolIcon;
  if (a.startsWith("10+")) return SportsEsportsIcon;
  return PersonIcon;
};

export function getTagPalette(type: LyricsChipTagType, category: string): TagPalette {
  const keywordIcon = getKeywordIcon(category);
  switch (type) {
    case "targetAge":
      return {
        icon: getTargetAgeIcon(category),
        color: "primary.main",
        contrastText: "primary.contrastText",
        hoverColor: "primary.dark",
      };
    case "mood":
      return {
        icon: getMoodIcon(category),
        color: "secondary.main",
        contrastText: "secondary.contrastText",
        hoverColor: "secondary.dark",
      };
    case "category":
      return {
        icon: getKeywordIcon(category),
        color: "info.main",
        contrastText: "info.contrastText",
        hoverColor: "info.dark",
      };
    case "keyword":
    default:
      return {
        icon: keywordIcon,
        color: "inherit",
        contrastText: "inherit",
        hoverColor: "inherit",
      };
  }
}

export default getTagPalette;
